import React, { useEffect, useState, useRef } from 'react'
import { createRoot } from "react-dom/client"
import './CareersForm.scss'
import { loadCaptchaEnginge, LoadCanvasTemplate, validateCaptcha } from 'react-simple-captcha'
import axios from 'axios'
import FormError from './FormError'
import ModalFormResponse from '../components/modals/ModalFormResponse'

const CareersForm = ({ submitButton = 'Send' }) => {
  const firstName = useRef(null)
  const lastName = useRef(null)
  const city = useRef(null)
  const companyName = useRef(null)
  const jobTitle = useRef(null)
  const phone = useRef(null)
  const email = useRef(null)
  const about = useRef(null)
  const fileRef = useRef(null)
  const captcha = useRef(null)
  const [formState, setFormState] = useState('populate')
  const [file, setFile] = useState<File | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState([])
  const ourCompanyName = process.env.REACT_APP_COMPANY_NAME

  useEffect(() => {
    loadCaptchaEnginge(6)
  }, [])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFile(e.target.files[0])
    }
  }
  const readUploadedFileAsText = (inputFile) => {
    const temporaryFileReader = new FileReader()

    return new Promise((resolve, reject) => {
      temporaryFileReader.onerror = () => {
        temporaryFileReader.abort()
        reject(new DOMException("Problem parsing input file."))
      }

      temporaryFileReader.onload = () => {
        resolve(temporaryFileReader.result)
      }
      temporaryFileReader.readAsArrayBuffer(inputFile)
    })
  }

  const fileValidation = async (file) => {

    try {
      const fileAsArr = await readUploadedFileAsText(file)
      const arr = new Uint8Array(fileAsArr as ArrayBuffer).subarray(0, 4)
      let header = "";
      for (let i = 0; i < arr.length; i++) {
        header += arr[i].toString(16)
      }

      // Check the file signature against known types
      header = header.toLowerCase()
      let allowed = false;
      switch (header) {
        case '25504446': //pdf
        case 'ffd8ffe0': //image/jpeg
        case 'ffd8ffe1': //image/jpeg
        case 'ffd8ffe2': //image/jpeg
        case 'd0cf11e0': //msWord(doc)
        case '0d444f43': //msWord(doc)
        case 'cf11e0a1': //msWord(doc)
        case 'dba52d00': //msWord(doc)
        case '504b34': //msWord(docx)
          allowed = true
          break;

        default:
          allowed = false
      }
      return allowed

    } catch (e) {
      return false
    }
  }

  const validation = async () => {

    let resp = true
    let errors = []

    const fileCheck = await fileValidation(file)

    if (firstName.current.value.length < 3) {
      errors.push({ firstName: 'Missing first name.' })
    }
    if (lastName.current.value.length < 3) {
      errors.push({ lastName: 'Missing last name.' })
    }
    if (!email.current.value.match(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/)) {
      errors.push({ email: 'Missing email address.' })
    }
    if (companyName.current.value.length < 3) {
      errors.push({ companyName: 'Missing company name.' })
    }
    if (city.current.value.length < 3) {
      errors.push({ city: 'Missing city name.' })
    }
    if (phone.current.value.length < 6) {
      errors.push({ phone: 'Please provide your phone number.' })
    }
    if (about.current.value.length < 4) {
      errors.push({ about: 'Missing short about yourself.' })
    }
    if (!fileCheck) {
      errors.push({ file: 'Missing CV document.' })
    }
    if (!validateCaptcha(captcha.current.value)) {
      errors.push({ captcha: 'Please provide captcha code.' })
    }

    setError(errors)

    if (errors.length > 0) {
      resp = false
    }
    return resp
  }

  const handleFormSubmission = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    const doValidation = await validation()

    //passed form mandatory fields
    if (doValidation) {
      let data = new FormData()
      data.append('file', file)
      data.append('firstName', firstName.current.value)
      data.append('lastName', lastName.current.value)
      data.append('companyName', companyName.current.value)
      data.append('jobTitle', jobTitle.current.value)
      data.append('phone', phone.current.value)
      data.append('city', city.current.value)
      data.append('email', email.current.value)
      data.append('about', about.current.value)

      const authString = btoa(process.env.REACT_APP_API_USERNAME + ":" + process.env.REACT_APP_API_PASSWORD)
      const db = await axios.post(process.env.REACT_APP_API_ENDPOINT + '/forms/careers', data, {
        headers: {
          'Content-type': 'multipart/form-data',
          'Authorization': `Basic ${authString}`,
        }
      })

      //success api
      if (!db.data.error) {
        setFormState('sent')
        const modalTitle = <span className='orange'>Hi {firstName.current.value}</span>
        const modalMessage = <>Thank you for taking the time to apply for the <span className='job-title'>{jobTitle.current.value}</span> position at {ourCompanyName}.<br /><br />We appreciate your
          interest
          in joining the team and the effort you put into submitting your application with us.</>
        createRoot(document.getElementById('modal-responses')).render(<ModalFormResponse status={true} message={modalMessage} title={modalTitle} />)
        firstName.current.value = ''
        lastName.current.value = ''
        companyName.current.value = ''
        jobTitle.current.value = ''
        phone.current.value = ''
        city.current.value = ''
        email.current.value = ''
        about.current.value = ''
        captcha.current.value = ''
        fileRef.current.value = null
        setSubmitting(false)
      }
    } else {
      document.querySelector('#js-scroll-anchor').scrollIntoView({
        behavior: 'smooth'
      })
      setSubmitting(false)

    }
  }

  return (
    <>
      <div className="careers-form form-holder" id="js-scroll-anchor">
        <form autoComplete={'one-time-code'}>
          <div className="form-group">
            <label>First name *
              <input name="firstName" ref={firstName} type="text" id="firstName" autoComplete={'one-time-code'} /></label>
            {error.findIndex((temp) => temp['firstName']) !== -1 && (
              <FormError errorString='Missing first name.' />
            )}
          </div>
          <div className="form-group">
            <label>Family Name *
              <input name="lastName" ref={lastName} type="text" id="lastName" autoComplete={'one-time-code'} /></label>
            {error.findIndex((temp) => temp['lastName']) !== -1 && (
              <FormError errorString='Missing last name.' />
            )}
          </div>

          <div className="form-group">
            <label>Email *
              <input name="email" ref={email} type="text" id="email" autoComplete={'one-time-code'} /></label>
            {error.findIndex((temp) => temp['email']) !== -1 && (
              <FormError errorString='Missing or wrong email address.' />
            )}
          </div>

          <div className="form-group">
            <label>Company Name *
              <input name="companyName" ref={companyName} type="text" id="companyName" autoComplete={'one-time-code'} /></label>
            {error.findIndex((temp) => temp['companyName']) !== -1 && (
              <FormError errorString='Missing company name.' />
            )}
          </div>

          <div className="form-group">
            <label>Job Title
              <input name="jobTitle" ref={jobTitle} type="text" id="jobTitle" autoComplete={'one-time-code'} /></label>
          </div>

          <div className="form-group">
            <label htmlFor="city">City *
              <input name="city" ref={city} type="text" id="city" autoComplete={'one-time-code'} /></label>
            {error.findIndex((temp) => temp['city']) !== -1 && (
              <FormError errorString='Missing city.' />
            )}
          </div>

          <div className="form-group">
            <label>Phone *
              <input name="phone" ref={phone} type="text" id="phone" autoComplete={'one-time-code'} /></label>
            {error.findIndex((temp) => temp['phone']) !== -1 && (
              <FormError errorString='Missing phone number.' />
            )}
          </div>

          <div className="form-group">
            <label>Short about yourself *
              <input name="about" ref={about} id="about" autoComplete={'one-time-code'} /></label>
            {error.findIndex((temp) => temp['about']) !== -1 && (
              <FormError errorString='Missing short about yourself.' />
            )}
          </div>

          <div className="form-group">
            <label>Your CV *
              <input type="file" ref={fileRef} onChange={handleFileChange} id="file" autoComplete={'one-time-code'} /></label>
            {error.findIndex((temp) => temp['file']) !== -1 && (
              <FormError errorString='Missing CV document.' />
            )}
          </div>

          <div className="form-group">
            <label>Captcha Code *
              <input name="captcha" ref={captcha} id="captcha" autoComplete={'one-time-code'} /></label>
            {error.findIndex((temp) => temp['captcha']) !== -1 && (
              <FormError errorString='Wrong captcha code.' />
            )}
          </div>
          <div className="form-group"><LoadCanvasTemplate /></div>

          <button onClick={handleFormSubmission} disabled={submitting} className="button">
            {submitting ? <div className='spinner'></div> : submitButton}
          </button>
        </form>
      </div>
    </>
  )
}
export default CareersForm